import { useEffect, useRef, useState } from "react";
import { ChatId, type ChatMessage, type EquipmentType, Filter, type MessageId, role, Source, status } from "../model";
import { AssistantId, loadAssistants } from "@genai/assistant";
import { FetchEventSource } from "@genai/http-client";
import { useNavigate, useParams } from "react-router-dom";
import { createMessage } from "./createMessageFn";
import { toSource } from "./toSource";
import { loadChat, loadMessages } from "../loaders";
import { useQuery } from "@tanstack/react-query";
import { CollectionId, loadFiles, StoredFile } from "@genai/collection";
import { createChat } from "./createChat";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.mjs?worker";
import { listFiles } from "../../collection/src/listFiles.ts";
import { getBrandsAndModels } from "./getBrandsAndModels.ts";
import { Brand } from "../../file/model";

pdfjsLib.GlobalWorkerOptions.workerPort = new pdfjsWorker(); // TODO useful?

export function useChat(equipmentType?: EquipmentType) {
  const navigate = useNavigate();
  const collectionId = useRef<CollectionId | undefined>();

  const { assistantId, chatId } = useParams() as {
    assistantId: AssistantId;
    chatId: ChatId | undefined;
  };

  // Chat not existing, creating new chat
  const { data: assistants } = useQuery({
    queryKey: ["assistants"],
    queryFn: () => !chatId ? loadAssistants() : undefined
  });

  const currentAssistant = assistants?.find((assistant) => assistant.uuid === assistantId);
  collectionId.current = currentAssistant?.collection ?? undefined;

  const { data: fileList } = useQuery({
    queryKey: ["fileList", assistantId, equipmentType],
    enabled: !!collectionId.current,
    queryFn: () => collectionId.current ? listFiles(collectionId.current, true, true, equipmentType) : []
  });

  const brands: Brand[] = getBrandsAndModels(fileList);

  // Existing chat, loading messages
  const { data: initialMessages, isLoading: isChatLoading } = useQuery({
    queryKey: ["messages", assistantId, chatId],
    queryFn: () =>
      chatId ? loadMessages(assistantId as AssistantId, chatId as ChatId) : []
  });


  const { data: currentChat } = useQuery({
    queryKey: ["chat", assistantId, chatId],
    queryFn: () => chatId ? loadChat(assistantId, chatId) : null

  });

  collectionId.current = currentChat?.collection;

  const { data: initialFiles } = useQuery({
    queryKey: ["files", currentChat?.collection],
    queryFn: () =>
      currentChat?.collection ? loadFiles(currentChat?.collection) : []
  });

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(initialMessages || []);
  const [files, setFiles] = useState<StoredFile[]>(initialFiles || []);

  useEffect(() => {
    setFiles(initialFiles || []);
  }, [initialFiles]);

  useEffect(() => {
    setChatMessages((state) => [
      ...(initialMessages || []),
      ...state.filter((message) => message.status === status.pending)
    ]);
  }, [initialMessages]);

  const appendFromStream = (
    source: EventSource,
    onEnd: () => void,
    parseResponse: (event: MessageEvent) => string = (event) =>
      JSON.parse(event.data).message,
    parseSources: (event: CustomEvent) => Source[] = (event) =>
      JSON.parse(event.detail).sources.map(toSource),
    parseUuid: (event: CustomEvent) => MessageId = (event) =>
      JSON.parse(event.detail).id
  ) => {
    let tempMessage: ChatMessage = {
      uuid: crypto.randomUUID() as MessageId,
      role: role.assistant,
      content: "",
      status: status.pending
    };
    setChatMessages((state) => [...state, tempMessage]);

    source.addEventListener("message", function(event) {
      const chunk = parseResponse(event);
      tempMessage = {
        ...tempMessage,
        content: [tempMessage.content, chunk].join(""),
        status: status.streaming
      };
      setChatMessages((state) => [
        ...state.filter((message) => message.uuid !== tempMessage.uuid),
        tempMessage
      ]);
    });
    source.addEventListener("uuid", function(event: Event) {
      const uuid = parseUuid(event as CustomEvent);
      const oldUuid = tempMessage.uuid;
      tempMessage = {
        ...tempMessage,
        uuid
      };
      setChatMessages((state) => [
        ...state.filter((message) => message.uuid !== oldUuid),
        tempMessage
      ]);
    });

    source.addEventListener("sources", function(event: Event) {
      tempMessage = {
        ...tempMessage,
        sources: parseSources(event as CustomEvent)
      };
      setChatMessages((state) => [
        ...state.filter((message) => message.uuid !== tempMessage.uuid),
        tempMessage
      ]);
    });

    source.addEventListener("end", async function() {
      tempMessage = {
        ...tempMessage,
        status: status.success
      };
      setChatMessages((state) => [
        ...state.filter((message) => message.uuid !== tempMessage.uuid),
        tempMessage
      ]);
      onEnd();
    });

    source.addEventListener("error", function(event) {
      tempMessage = {
        ...tempMessage,
        error: "message" in event ? (event.message as string) : "",
        status: status.error
      };
      setChatMessages((state) => [
        ...state.filter((message) => message.uuid !== tempMessage.uuid),
        tempMessage
      ]);
    });
  };

  const appendOptimistic = (content: string, filters?: Filter) => {
    const tempMessageId = crypto.randomUUID() as MessageId;
    setChatMessages((state) => [
      ...state,
      {
        uuid: tempMessageId,
        role: role.user,
        content,
        status: status.pending,
        filters
      }
    ]);

    const validate = (message: ChatMessage) => {
      setChatMessages((state) => {
        const pendingMessages = state.filter(
          (message) =>
            message.uuid !== tempMessageId && message.status === status.pending
        );
        return [
          ...state.filter(
            (message) =>
              message.uuid !== tempMessageId &&
              message.status !== status.pending
          ),
          message,
          ...pendingMessages
        ];
      });
    };

    const fail = (error: string) => {
      setChatMessages((state) => {
        const failedMessage = {
          ...(state.find(
            (message) => message.uuid === tempMessageId
          ) as ChatMessage),
          status: status.error,
          error: error
        };
        return [
          ...state.filter((message) => message.uuid !== tempMessageId),
          failedMessage
        ];
      });
    };

    return [validate, fail] as const;
  };

  if (!chatId) {
    collectionId.current = assistants?.find((assistant) => assistant.uuid === assistantId)?.collection ?? undefined;
  }

  const createNewChat = async (assistantId: AssistantId, brand: string, model: string) => {
    const newChat = await createChat(
      assistantId,
      brand,
      model,
    );

    return [
      () => navigate(`/assistant/${assistantId}/chat/${newChat.uuid}`)
    ] as const;
  };

  /**
   * Sends a message or files to a specific chat.
   *
   * This method either creates a new chat or uses an existing one to send a text message
   * and/or files. If no chat exists, a new one is created. The message or files are then
   * sent, and the user is navigated to the active chat.
   */
  const sendMessage = async (content: string, filters?: Filter, chatId?: ChatId) => {
    if (!content) {
      return;
    }

    const callbackChatCurrent = () => navigate(`/assistant/${assistantId}/chat/${chatId}`);

    const [validate, fail] = appendOptimistic(content, filters);
    if (!chatId) {
      fail("Impossible de créer la discussion");
      return;
    }

    try {
      const responseMessage = await createMessage(
        assistantId,
        chatId,
        content,
        filters
      );

      if (!responseMessage) {
        fail("Failed to create message");
        return;
      }

      validate({
        ...responseMessage,
        status: status.success
      });

      if (!(filters?.hidden === "true")) {
        const source = new FetchEventSource(
          `/assistants/${assistantId}/chats/${chatId}/response/streaming`
        ) as EventSource;
        appendFromStream(source, callbackChatCurrent);
      } else {
        callbackChatCurrent();
      }
    } catch (e) {
      fail((e as Error).message);
    }
  };

  return { chatMessages, files, sendMessage, isChatLoading, brands, createNewChat } as const;
}
